import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getStoredUser, subscribeAuthChanged } from '../../lib/appAuth';
import { useCurrency } from '../../context/CurrencyContext';
import './Header.css';

const Header = ({ onLogoClick, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('home');
  const [user, setUser] = useState(() => getStoredUser());
  const headerRef = useRef(null);
  const { selectedCurrency, setSelectedCurrency, supportedCurrencies } = useCurrency();

  useEffect(() => {
    const syncUser = () => setUser(getStoredUser());
    syncUser();
    return subscribeAuthChanged(syncUser);
  }, []);

  // Update selected tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '') {
      setSelectedTab('home');
    } else if (path.startsWith('/hotels')) {
      setSelectedTab('');
    } else if (path.startsWith('/flights')) {
      setSelectedTab('flights');
    } else if (path === '/about') {
      setSelectedTab('about');
    } else if (path === '/contact') {
      setSelectedTab('contact');
    } else if (path === '/profile') {
      setSelectedTab('profile');
    } else {
      setSelectedTab('home');
    }
  }, [location.pathname]);

  const handleLogoClick = (e) => {
    if (onLogoClick) {
      e.preventDefault();
      onLogoClick();
    } else {
      navigate('/');
      window.scrollTo(0, 0);
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const goNav = (tab, path) => {
    setSelectedTab(tab);
    navigate(path);
    window.scrollTo(0, 0);
    handleNavLinkClick();
  };

  return (
    <header className="header" ref={headerRef}>
      <div className="header__top">
      <div className="header__container">
        <div className="header__logo">
          <Link to="/" className="header__logo-link">
            <h1 
              className="header__logo-text"
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            >
              RezHolidays
            </h1>
          </Link>
        </div>
        
          <div className="header__top-actions">
            <div className="header__currency-wrap">
              <label htmlFor="currency-switcher" className="header__currency-label">
                Currency
              </label>
              <select
                id="currency-switcher"
                className="header__currency-select"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                aria-label="Select display currency"
              >
                {supportedCurrencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            {user ? (
              <Link to="/profile" className="header__profile-btn">
                Profile
              </Link>
            ) : (
              <>
                <Link to="/register" className="header__register-btn">
                  Register
                </Link>
                <Link to="/signin" className="header__signin-btn">
                  Sign in
                </Link>
              </>
            )}
            <button
              type="button"
              className="header__menu-toggle"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-controls="header-mobile-nav"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <span className="header__menu-toggle-bars" aria-hidden>
                <span className="header__menu-toggle-bar" />
                <span className="header__menu-toggle-bar" />
                <span className="header__menu-toggle-bar" />
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        id="header-mobile-nav"
        className={`header__mobile-dropdown ${isMobileMenuOpen ? 'header__mobile-dropdown--open' : ''}`}
      >
        <nav className="header__mobile-dropdown-nav" aria-label="Main">
          <button
            type="button"
            className={`header__mobile-link ${selectedTab === 'home' ? 'header__mobile-link--active' : ''}`}
            onClick={() => goNav('home', '/')}
          >
            <span className="header__tab-icon">🏠</span>
            Home
          </button>
          <button
            type="button"
            className={`header__mobile-link ${selectedTab === 'flights' ? 'header__mobile-link--active' : ''}`}
            onClick={() => goNav('flights', '/flights')}
          >
            <span className="header__tab-icon">✈️</span>
            Flights
          </button>
          <button
            type="button"
            className={`header__mobile-link ${selectedTab === 'about' ? 'header__mobile-link--active' : ''}`}
            onClick={() => goNav('about', '/about')}
          >
            <span className="header__tab-icon">ℹ️</span>
            About
          </button>
          <button
            type="button"
            className={`header__mobile-link ${selectedTab === 'contact' ? 'header__mobile-link--active' : ''}`}
            onClick={() => goNav('contact', '/contact')}
          >
            <span className="header__tab-icon">📧</span>
            Contact Us
          </button>
          {user && (
            <button
              type="button"
              className={`header__mobile-link ${selectedTab === 'profile' ? 'header__mobile-link--active' : ''}`}
              onClick={() => goNav('profile', '/profile')}
            >
              <span className="header__tab-icon">👤</span>
              Profile
            </button>
          )}
        </nav>
      </div>
      
      <div className="header__tabs">
        <div className="header__container">
          <nav className="header__tabs-nav">
            <button 
              className={`header__tab ${selectedTab === 'home' ? 'header__tab--active' : ''}`}
              onClick={() => {
                setSelectedTab('home');
                navigate('/');
                window.scrollTo(0, 0);
                handleNavLinkClick();
              }}
            >
              <span className="header__tab-icon">🏠</span>
              <span>Home</span>
            </button>
            <button 
              className={`header__tab ${selectedTab === 'flights' ? 'header__tab--active' : ''}`}
              onClick={() => {
                setSelectedTab('flights');
                navigate('/flights');
                window.scrollTo(0, 0);
                handleNavLinkClick();
              }}
            >
              <span className="header__tab-icon">✈️</span>
              <span>Flights</span>
            </button>
            <button 
              className={`header__tab ${selectedTab === 'about' ? 'header__tab--active' : ''}`}
              onClick={() => {
                setSelectedTab('about');
                navigate('/about');
                window.scrollTo(0, 0);
                handleNavLinkClick();
              }}
            >
              <span className="header__tab-icon">ℹ️</span>
              <span>About</span>
            </button>
            <button 
              className={`header__tab ${selectedTab === 'contact' ? 'header__tab--active' : ''}`}
              onClick={() => {
                setSelectedTab('contact');
                navigate('/contact');
                window.scrollTo(0, 0);
                handleNavLinkClick();
              }}
            >
              <span className="header__tab-icon">📧</span>
              <span>Contact Us</span>
            </button>
            {user && (
              <button
                className={`header__tab ${selectedTab === 'profile' ? 'header__tab--active' : ''}`}
                onClick={() => {
                  setSelectedTab('profile');
                  navigate('/profile');
                  window.scrollTo(0, 0);
                  handleNavLinkClick();
                }}
              >
                <span className="header__tab-icon">👤</span>
                <span>Profile</span>
              </button>
            )}
        </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
